import type { TriangleVertices } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import {
    buildTriangleSpatialTree,
    computeClosestDistance,
    computeClosestPerpendicularDistance,
    indexTriangles,
    type TriangleSpatialNode,
} from './TriangleSpatialTree';

type BoundingBox = {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
};

type TriangleBuilding = {
    bounds: BoundingBox;
    centerX: number;
    centerY: number;
    centerZ: number;
    tree?: TriangleSpatialNode;
};

type BuildingSpatialNode = {
    bounds: BoundingBox;
    buildings?: TriangleBuilding[];
    left?: BuildingSpatialNode;
    right?: BuildingSpatialNode;
};

export type BuildingTriangleSpatialTree = {
    root?: BuildingSpatialNode;
    maxDistance: number;
};

const BUILDING_BVH_LEAF_SIZE = 8;

export function buildBuildingTriangleSpatialTree(triangleGroups: TriangleVertices[][], maxDistance: number): BuildingTriangleSpatialTree {
    const buildings: TriangleBuilding[] = [];

    for (const triangles of triangleGroups) {
        if (triangles.length === 0) {
            continue;
        }

        const indexedTriangles = indexTriangles(triangles);
        const bounds = mergeTriangleBounds(triangles);
        buildings.push({
            bounds,
            centerX: (bounds.minX + bounds.maxX) / 2,
            centerY: (bounds.minY + bounds.maxY) / 2,
            centerZ: (bounds.minZ + bounds.maxZ) / 2,
            tree: buildTriangleSpatialTree(indexedTriangles),
        });
    }

    return {
        root: buildBuildingNode(buildings),
        maxDistance,
    };
}

export function computeClosestBuildingDistance(position: FVector, tree?: BuildingTriangleSpatialTree): number {
    if (!tree) {
        return Infinity;
    }

    if (!tree.root) {
        return tree.maxDistance;
    }

    return computeClosestDistanceWithBuildings(position, tree, computeClosestDistance);
}

export function computeClosestBuildingPerpendicularDistance(position: FVector, tree?: BuildingTriangleSpatialTree): number {
    if (!tree) {
        return Infinity;
    }

    if (!tree.root) {
        return tree.maxDistance;
    }

    return computeClosestDistanceWithBuildings(position, tree, computeClosestPerpendicularDistance);
}

function computeClosestDistanceWithBuildings(
    position: FVector,
    tree: BuildingTriangleSpatialTree,
    getTriangleDistance: (position: FVector, tree?: TriangleSpatialNode, maxDistance?: number) => number
): number {
    let closestDistance = tree.maxDistance;
    visit(tree.root!, distanceToBoundingBox(position, tree.root!.bounds));
    return closestDistance;

    function visit(node: BuildingSpatialNode, nodeDistance: number): boolean {
        if (nodeDistance >= closestDistance) {
            return false;
        }

        if (node.buildings) {
            for (const building of node.buildings) {
                const buildingDistance = distanceToBoundingBox(position, building.bounds);
                if (buildingDistance >= closestDistance) {
                    continue;
                }

                const triangleDistance = getTriangleDistance(position, building.tree, closestDistance);
                if (triangleDistance < closestDistance) {
                    closestDistance = triangleDistance;
                    if (closestDistance === 0) {
                        return true;
                    }
                }
            }
            return false;
        }

        const leftNode = node.left;
        const rightNode = node.right;

        if (leftNode && rightNode) {
            const leftDistance = distanceToBoundingBox(position, leftNode.bounds);
            const rightDistance = distanceToBoundingBox(position, rightNode.bounds);

            if (leftDistance <= rightDistance) {
                if (visit(leftNode, leftDistance)) {
                    return true;
                }
                if (rightDistance < closestDistance) {
                    return visit(rightNode, rightDistance);
                }
                return false;
            }

            if (visit(rightNode, rightDistance)) {
                return true;
            }
            if (leftDistance < closestDistance) {
                return visit(leftNode, leftDistance);
            }
            return false;
        }

        if (leftNode) {
            return visit(leftNode, distanceToBoundingBox(position, leftNode.bounds));
        }

        if (rightNode) {
            return visit(rightNode, distanceToBoundingBox(position, rightNode.bounds));
        }

        return false;
    }
}

function buildBuildingNode(buildings: TriangleBuilding[]): BuildingSpatialNode | undefined {
    if (buildings.length === 0) {
        return undefined;
    }

    const bounds = mergeBuildingBounds(buildings);
    if (buildings.length <= BUILDING_BVH_LEAF_SIZE) {
        return { bounds, buildings };
    }

    const axis = getLongestAxis(bounds);
    const sortedBuildings = [...buildings].sort((left, right) => getBuildingCentroid(left, axis) - getBuildingCentroid(right, axis));
    const middleIndex = Math.floor(sortedBuildings.length / 2);
    const leftBuildings = sortedBuildings.slice(0, middleIndex);
    const rightBuildings = sortedBuildings.slice(middleIndex);

    if (leftBuildings.length === 0 || rightBuildings.length === 0) {
        return { bounds, buildings: sortedBuildings };
    }

    return {
        bounds,
        left: buildBuildingNode(leftBuildings),
        right: buildBuildingNode(rightBuildings),
    };
}

function mergeTriangleBounds(triangles: TriangleVertices[]): BoundingBox {
    const [firstTriangle, ...restTriangles] = triangles;
    const bounds = getTriangleBounds(firstTriangle);

    for (const triangle of restTriangles) {
        const triangleBounds = getTriangleBounds(triangle);
        bounds.minX = Math.min(bounds.minX, triangleBounds.minX);
        bounds.minY = Math.min(bounds.minY, triangleBounds.minY);
        bounds.minZ = Math.min(bounds.minZ, triangleBounds.minZ);
        bounds.maxX = Math.max(bounds.maxX, triangleBounds.maxX);
        bounds.maxY = Math.max(bounds.maxY, triangleBounds.maxY);
        bounds.maxZ = Math.max(bounds.maxZ, triangleBounds.maxZ);
    }

    return bounds;
}

function mergeBuildingBounds(buildings: TriangleBuilding[]): BoundingBox {
    const [firstBuilding, ...restBuildings] = buildings;
    const bounds = { ...firstBuilding.bounds };

    for (const building of restBuildings) {
        bounds.minX = Math.min(bounds.minX, building.bounds.minX);
        bounds.minY = Math.min(bounds.minY, building.bounds.minY);
        bounds.minZ = Math.min(bounds.minZ, building.bounds.minZ);
        bounds.maxX = Math.max(bounds.maxX, building.bounds.maxX);
        bounds.maxY = Math.max(bounds.maxY, building.bounds.maxY);
        bounds.maxZ = Math.max(bounds.maxZ, building.bounds.maxZ);
    }

    return bounds;
}

function getBuildingCentroid(building: TriangleBuilding, axis: 'x' | 'y' | 'z'): number {
    switch (axis) {
        case 'x':
            return building.centerX;
        case 'y':
            return building.centerY;
        default:
            return building.centerZ;
    }
}

function getLongestAxis(bounds: BoundingBox): 'x' | 'y' | 'z' {
    const xLength = bounds.maxX - bounds.minX;
    const yLength = bounds.maxY - bounds.minY;
    const zLength = bounds.maxZ - bounds.minZ;

    if (xLength >= yLength && xLength >= zLength) {
        return 'x';
    }
    if (yLength >= zLength) {
        return 'y';
    }
    return 'z';
}

function getTriangleBounds(triangle: TriangleVertices): BoundingBox {
    return {
        minX: Math.min(triangle.A.x, triangle.B.x, triangle.C.x),
        minY: Math.min(triangle.A.y, triangle.B.y, triangle.C.y),
        minZ: Math.min(triangle.A.z, triangle.B.z, triangle.C.z),
        maxX: Math.max(triangle.A.x, triangle.B.x, triangle.C.x),
        maxY: Math.max(triangle.A.y, triangle.B.y, triangle.C.y),
        maxZ: Math.max(triangle.A.z, triangle.B.z, triangle.C.z),
    };
}

function distanceToBoundingBox(position: FVector, bounds: BoundingBox): number {
    const dx = Math.max(bounds.minX - position.x, 0, position.x - bounds.maxX);
    const dy = Math.max(bounds.minY - position.y, 0, position.y - bounds.maxY);
    const dz = Math.max(bounds.minZ - position.z, 0, position.z - bounds.maxZ);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
